import React, { useState, useEffect, useRef } from 'react';
import { reservationApi } from '../../api/api';
import './reservationsappel.css';
import Header from '../Header/Header';
import Footer from '../footer/footer';
import TopAnnouncementBar from '../TopAnnouncementBar/TopAnnouncementBar';
import WhatsAppButton from '../whatsapp/WhatsAppButton';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MdPhoneInTalk, 
  MdEmail, 
  MdPerson, 
  MdMessage, 
  MdCheckCircle, 
  MdErrorOutline,
  MdAutoAwesome,
  MdOutlineSupportAgent,
  MdOutlineSpeed,
  MdOutlineSecurity
} from 'react-icons/md';
import { GiAstronautHelmet, GiStarsStack, GiGalaxy, GiSpiralShell, GiCrystalBall } from 'react-icons/gi';

const ReservationsAppel = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    date_appel: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    sujet: "Demande d'information",
    notes: '',
    status: 'en_attente'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  let cursorTimeout;

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const validateField = (name, value) => {
    let error = '';
    
    switch(name) {
      case 'nom':
        if (!value.trim()) error = 'Le nom est requis';
        else if (value.length < 2) error = 'Nom trop court';
        break;
      case 'email':
        if (!value) error = 'Email requis';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Email invalide';
        break;
      case 'telephone':
        if (!value) error = 'Téléphone requis';
        else if (!/^[0-9+ ]{10,15}$/.test(value)) error = 'Format invalide';
        break;
      case 'date_appel':
        if (!value) error = 'Date requise';
        else if (new Date(value) < new Date()) error = 'Date doit être future';
        break;
      case 'sujet':
        if (!value.trim()) error = 'Sujet requis';
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (errors[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(key => {
      if (key !== 'notes' && key !== 'status') {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);
    setSubmitSuccess(false);

    try {
      await reservationApi.createReservation(formData);
      
      setSubmitSuccess(true);
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        date_appel: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
        sujet: "Demande d'information",
        notes: '',
        status: 'en_attente'
      });
      
    } catch (err) {
      console.error('Erreur:', err);
      setApiError(err.message.includes('date appel') 
        ? 'Format de date invalide. Veuillez sélectionner une date valide.'
        : err.message || 'Erreur lors de l\'envoi du formulaire');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reservation-appel-container" ref={sectionRef}>
      <TopAnnouncementBar />
      <WhatsAppButton />
      <Header />
      
      {/* Cursor Glow */}
      <motion.div 
        className={`cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 300,
          y: mousePosition.y - 300,
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 200,
          mass: 0.5
        }}
      />

      {/* Particles */}
      <div className="particles-container">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 20}s`,
              animationDuration: `${25 + Math.random() * 30}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
            }}
          />
        ))}
      </div>

      {/* Animated Orbs */}
      <div className="animated-orbs">
        <div className="orb orb-1" style={{ transform: `translate(${scrollProgress * 120}px, ${scrollProgress * 60}px)` }} />
        <div className="orb orb-2" style={{ transform: `translate(-${scrollProgress * 100}px, -${scrollProgress * 70}px)` }} />
        <div className="orb orb-3" style={{ transform: `translate(${scrollProgress * 80}px, -${scrollProgress * 50}px)` }} />
        <div className="orb orb-4" style={{ transform: `translate(-${scrollProgress * 50}px, ${scrollProgress * 80}px)` }} />
      </div>

      {/* Floating Elements */}
      <div className="floating-elements">
        <GiGalaxy className="floating floating-1" />
        <GiSpiralShell className="floating floating-2" />
        <GiAstronautHelmet className="floating floating-3" />
        <GiStarsStack className="floating floating-4" />
        <GiCrystalBall className="floating floating-5" />
      </div>
      
      <div className="reservation-appel-content">
        <div className="reservation-appel-card">
          <div className="reservation-appel-header">
            <motion.div 
              className="expertise-badge-wrapper"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="expertise-badge">
                <GiAstronautHelmet className="expertise-badge-icon" />
                <span>Prenez rendez-vous</span>
                <GiStarsStack className="expertise-badge-icon" />
              </span>
              <div className="expertise-badge-glow"></div>
            </motion.div>
            
            <motion.h2 
              className="reservation-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span className="title-line">
                <span className="title-gradient">Planifiez</span> un appel
              </span>
              <span className="title-line">
                avec notre <span className="title-highlight">équipe</span>
              </span>
              <div className="title-underline"></div>
            </motion.h2>
            
            <motion.p 
              className="reservation-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Discutons de votre projet et trouvons ensemble les meilleures solutions 
              <br />pour propulser votre entreprise vers de nouveaux sommets.
            </motion.p>
          </div>

          <AnimatePresence>
            {submitSuccess && (
              <motion.div 
                className="alert alert-success"
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -20, height: 0 }}
              >
                <MdCheckCircle />
                <span>Votre demande a été enregistrée avec succès! Un email de confirmation vous a été envoyé.</span>
              </motion.div>
            )}

            {apiError && (
              <motion.div 
                className="alert alert-error"
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -20, height: 0 }}
              >
                <MdErrorOutline />
                <span>{apiError}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="reservation-form">
            <input type="hidden" name="date_appel" value={formData.date_appel} />
            <input type="hidden" name="sujet" value={formData.sujet} />

            <div className="form-group">
              <label><MdPerson className="input-icon" /> Nom complet *</label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.nom ? 'error' : ''}
                placeholder="Votre nom complet"
              />
              {errors.nom && <span className="error-message">{errors.nom}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><MdEmail className="input-icon" /> Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.email ? 'error' : ''}
                  placeholder="votre@email.com"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label><MdPhoneInTalk className="input-icon" /> Téléphone *</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.telephone ? 'error' : ''}
                  placeholder="+33 6 12 34 56 78"
                />
                {errors.telephone && <span className="error-message">{errors.telephone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label><MdMessage className="input-icon" /> Notes supplémentaires</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                placeholder="Décrivez brièvement votre projet ou vos questions..."
              />
            </div>

            <motion.button 
              type="submit" 
              disabled={isSubmitting}
              className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <MdAutoAwesome />
                  <span>Réserver mon appel</span>
                </>
              )}
            </motion.button>
          </form>

          {/* Features Section */}
          <div className="form-features">
            <div className="form-feature">
              <MdOutlineSpeed />
              <span>Réponse sous 24h</span>
            </div>
            <div className="form-feature">
              <MdOutlineSupportAgent />
              <span>Support dédié</span>
            </div>
            <div className="form-feature">
              <MdOutlineSecurity />
              <span>100% confidentiel</span>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ReservationsAppel;